import http, { handleHttpError, handleHttpResponse } from '@/services/http'
import { HTTPResponse } from '@/services/http/http.types'
import { ICmsAsset, ICmsConfig, CmsEntityId, IEmailSubscription, IEventProgramStatus } from './cms.types'

const CMS_PREFIX = '/v1/cms'
type CmsListParams = Record<string, string | number | boolean | null | undefined>

const getConfig = (): Promise<HTTPResponse<ICmsConfig>> => {
  return http.get(`${CMS_PREFIX}/config`).then(handleHttpResponse).catch(handleHttpError)
}

const list = <T,>(endpoint: string, params?: CmsListParams): Promise<HTTPResponse<T[]>> => {
  return http.get(`${CMS_PREFIX}/${endpoint}`, { params: clearEmptyParams(params) }).then(handleHttpResponse).catch(handleHttpError)
}

const create = <T,>(endpoint: string, payload: Record<string, unknown>): Promise<HTTPResponse<T>> => {
  return http.post(`${CMS_PREFIX}/${endpoint}`, payload).then(handleHttpResponse).catch(handleHttpError)
}

const update = <T,>(
  endpoint: string,
  id: CmsEntityId,
  payload: Record<string, unknown>
): Promise<HTTPResponse<T>> => {
  return http.patch(`${CMS_PREFIX}/${endpoint}/${id}`, payload).then(handleHttpResponse).catch(handleHttpError)
}

const remove = <T,>(endpoint: string, id: CmsEntityId): Promise<HTTPResponse<T>> => {
  return http.delete(`${CMS_PREFIX}/${endpoint}/${id}`).then(handleHttpResponse).catch(handleHttpError)
}

const listAssets = (): Promise<HTTPResponse<ICmsAsset[]>> => {
  return list<ICmsAsset>('assets')
}

const uploadAsset = (file: File): Promise<HTTPResponse<ICmsAsset>> => {
  const data = new FormData()
  data.append('file', file)
  return http.post(`${CMS_PREFIX}/assets`, data).then(handleHttpResponse).catch(handleHttpError)
}

const deleteAsset = (id: ICmsAsset['id']): Promise<HTTPResponse<ICmsAsset>> => {
  return remove<ICmsAsset>('assets', id)
}

const listEmailSubscriptions = (): Promise<HTTPResponse<IEmailSubscription[]>> => {
  return list<IEmailSubscription>('email-subscriptions')
}

const exportEmailSubscriptions = async (): Promise<HTTPResponse<Blob>> => {
  return http
    .get(`${CMS_PREFIX}/email-subscriptions/export`, { responseType: 'blob' })
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getEventProgram = (): Promise<HTTPResponse<IEventProgramStatus>> => {
  return http.get(`${CMS_PREFIX}/event-program`).then(handleHttpResponse).catch(handleHttpError)
}

const uploadEventProgram = (file: File): Promise<HTTPResponse<IEventProgramStatus>> => {
  const data = new FormData()
  data.append('file', file)
  return http.post(`${CMS_PREFIX}/event-program`, data).then(handleHttpResponse).catch(handleHttpError)
}

const resetEventProgram = (): Promise<HTTPResponse<IEventProgramStatus>> => {
  return http.delete(`${CMS_PREFIX}/event-program`).then(handleHttpResponse).catch(handleHttpError)
}

const clearEmptyParams = (params?: CmsListParams) => {
  if (!params) return undefined

  const nextParams = Object.entries(params).reduce<CmsListParams>((acc, [key, value]) => {
    if (value !== '' && value !== null && value !== undefined) {
      acc[key] = value
    }

    return acc
  }, {})

  return Object.keys(nextParams).length ? nextParams : undefined
}

const cmsApi = {
  getConfig,
  list,
  create,
  update,
  remove,
  listAssets,
  uploadAsset,
  deleteAsset,
  listEmailSubscriptions,
  exportEmailSubscriptions,
  getEventProgram,
  uploadEventProgram,
  resetEventProgram,
}
export default cmsApi
